'use client'
import { ErrorOutline } from '@mui/icons-material'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo, useState } from 'react'

import { FlightOffer, TableData } from '@/src/types/flightOffer'
import { isEmpty } from '@/src/utils/generalUtils'
import { OfferEmptyState } from '../../common/offerEmptyState/offerEmptyState'
import { OfferDetail } from './offerDetail'

dayjs.extend(customParseFormat)

interface OfferDetailWrapperProps {
    offer: FlightOffer
    locale: string
}

const OfferDetailWrapper: React.FC<OfferDetailWrapperProps> = ({ offer, locale }) => {
    const t = useTranslations('offers')
    const [copied, setCopied] = useState(false)

    // Format date specifically for DD-MM-YYYY format from API
    const formatOfferDate = useCallback((date: string, format: string = 'MMM D, YYYY') => {
        if (!date) return ''
        const parsed = dayjs(date, 'DD-MM-YYYY', 'true')
        return parsed.isValid() ? parsed.format(format) : date
    }, [])

    // Event handlers
    const handleCopyCode = useCallback(() => {
        if (offer?.couponCode) {
            navigator.clipboard.writeText(offer.couponCode)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }, [offer?.couponCode])

    // Process tables data
    const processedTables = useMemo(() => {
        if (!offer?.tables) return []
        return offer.tables.map((table: TableData) => ({
            ...table,
            hasData: table.data && table.data.length > 0
        }))
    }, [offer?.tables])

    // Error state component
    if (isEmpty(offer) || offer.error) {
        return (
            <OfferDetail
                isLoading={false}
                isError={true}
                locale={locale}
                t={t}
                formatDate={formatOfferDate}
                errorComponent={
                    <OfferEmptyState
                        icon={
                            <ErrorOutline
                                sx={{
                                    fontSize: { xs: 80, md: 60 },
                                    color: 'textColors.secondary',
                                    opacity: 0.8
                                }}
                            />
                        }
                        title={t('errorLoadingOfferDetails')}
                        description={t('errorLoadingOfferDescription')}
                        primaryAction={{
                            label: t('browseAllOffers'),
                            href: `/${locale}/offers`
                        }}
                    />
                }
            />
        )
    }

    return (
        <OfferDetail
            isLoading={false}
            isError={false}
            offer={offer}
            desktopImageUrl={offer?.desktopImageUrl}
            mobileImageUrl={offer?.mobileImageUrl}
            copied={copied}
            processedTables={processedTables}
            t={t}
            formatDate={formatOfferDate}
            onCopyCode={handleCopyCode}
            locale={locale}
        />
    )
}

export default OfferDetailWrapper
